import React, { useRef } from "react";
import { Button } from "antd";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";

const getPrintTime = () => {
  const now = new Date();

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return now.toLocaleString("en-US", options);
};



const ReceiptTemplate = React.forwardRef(({ order, items }, ref) => {
  const { CompanyName } = useSelector((state) => state.authReducer);

  // Format GST percentage if available
  const gstPercents = order?.TotalGST && order?.TotalAmountWithoutGST
    ? `(${((order.TotalGST / order.TotalAmountWithoutGST) * 100).toFixed(2)}%)`
    : "";

  // Organize items into parent-child structure
  const organizeOrderItems = (items) => {
    // First get all parent items (DealItemId is null)
    const parentItems = items.filter((item) => item.DealItemId === null);

    const result = [];

    for (const parent of parentItems) {
      // Find children - same RandomId AND DealItemId is NOT null AND not the parent itself
      const children = items.filter(
        (item) => item.RandomId === parent.RandomId && item.DealItemId !== null
      );

      result.push({
        parent,
        children,
      });
    }

    return result;
  };

  const organizedItems = organizeOrderItems(items || []);

  // Generate item rows with parent-child structure
  const itemBody = organizedItems?.map((group, groupIdx) => {
    const rows = [];

    // Parent item row
    rows.push(
      <div
        key={`parent-${groupIdx}`}
        className="flex justify-between text-sm text-black px-1 py-0.5"
      >
        <span className="w-[20%] font-semibold">{group.parent.Quantity}</span>
        <span className="w-[60%] font-medium text-left">
          {group.parent.ProductName}
          {group.parent.SizeName ? ` (${group.parent.SizeName})` : ""}
          {group.parent.FlavourName ? ` ${group.parent.FlavourName}` : ""}
          {group.parent.DiscountPercent > 0 && (
            <span className="text-[10px] ml-1">
              ({group.parent.DiscountPercent}% OFF)
            </span>
          )}
        </span>
        <span className="w-[20%] text-right font-semibold">
          Rs {group.parent.PriceWithGST}
        </span>
      </div>
    );

    // Child item rows
    if (group.children?.length > 0) {
      group.children.forEach((child, childIdx) => {
        rows.push(
          <div
            key={`child-${groupIdx}-${childIdx}`}
            className="flex justify-between text-sm text-black px-1 py-0.5"
          >
            <span className="w-[20%]"></span>
            <span className="w-[60%] text-left pl-4 font-normal">
              + {child.Quantity} {child.ProductName}
              {child.SizeName ? ` (${child.SizeName})` : ""}
              {child.FlavourName ? ` ${child.FlavourName}` : ""}
              {child.DiscountPercent > 0 && (
                <span className="text-[10px] ml-1">
                  ({child.DiscountPercent}% OFF)
                </span>
              )}
            </span>
            <span className="w-[20%] text-right font-normal">
              + Rs {child.PriceWithGST}
            </span>
          </div>
        );
      });
    }

    return rows;
  }).flat();


  // Delivery charges row if applicable
  const beforeBill = order?.DeliveryCharges ? (
    <tr>
      <td style={{ verticalAlign: "middle", fontWeight: 400, border: 0, color: "#000" }}>
        Delivery Charges
      </td>
      <td style={{ textAlign: "right", verticalAlign: "middle", fontWeight: 400, border: 0, color: "#000" }}>
        Rs {order.DeliveryCharges}
      </td>
    </tr>
  ) : null;

  return (
    <div
      ref={ref}
      style={{
        fontFamily: "Calibri",
        fontSize: "12px",
        margin: "30px",
        display: "none" // Hide from screen
      }}
      className="salesReceipt print-only"
    >
      <style>
        {`
          @media print {
            .print-only {
              display: block !important;
            }
            .no-print {
              display: none !important;
            }
          }
          @media screen {
            .print-only {
              display: none !important;
            }
          }
        `}
      </style>

      <div style={{ width: "100%", textAlign: "center" }}></div>




      <table style={{ width: "100%" }}>
        <thead>
          <tr>
            <th style={{
              textAlign: "center",
              marginBottom: 0,
              fontSize: "24px",
              fontFamily: "Calibri",
              fontWeight: 600,
              color: "#000",
              height: "24px"
            }}>
              {CompanyName}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th style={{
              textAlign: "center",
              fontSize: "11px",
              fontWeight: 400,
              border: 0,
              color: "#000"
            }}>
              {order?.BranchName || "Branch Address"}
            </th>
            <th style={{
              textAlign: "center",
              fontSize: "11px",
              fontWeight: 400,
              border: 0,
              color: "#000"
            }}>
              {order?.AreaName || "Branch Address"}
            </th>
          </tr>
        </tbody>
      </table>









      <div className="my-1 border-t-2 border-b-2 border-black text-center w-full">
        {/* Heading */}
        <div className="w-full text-center  font-semibold text-black text-sm border-b-2 border-black">
          PRE - PAYMENT BILL
        </div>

        {/* Order Mode Row */}
        <div className="flex justify-between text-sm text-black font-medium px-1">
          <strong>Order Mode</strong>
          <span>{order?.OrderMode || "N/A"}</span>
        </div>

        {/* Customer Details */}
        <div className="w-full flex flex-col mt-1 text-left text-sm text-black px-1 ">
          <div>
            <strong>Customer:</strong> &nbsp; {order?.CustomerName || "N/A"}
          </div>
          <div>
            <strong>Address:</strong>&nbsp;  {order?.CompleteAddress || "N/A"}
          </div>
          <div>
            <strong>Phone:</strong> &nbsp; {order?.PhoneNumber || "N/A"}
          </div>
          {order?.AlternateNumber && (
            <div>
              <strong>Alt:</strong>&nbsp;  {order?.AlternateNumber}
            </div>
          )}
          {order?.LandMark && (
            <div>
              <strong>LandMark:</strong>&nbsp;  {order?.LandMark}
            </div>
          )}
          {order?.Remarks1 && (
            <div>
              <strong>Special Instruction:</strong>&nbsp;  {order?.Remarks1}
            </div>
          )}
        </div>
      </div>




      <div className="w-full text-sm text-black px-1 ">
        <div className="flex justify-between font-medium">
          <span>Order #</span>
          <span>{order?.OrderNumber || "N/A"}</span>
        </div>
        <div className="flex justify-between font-medium">
          <span>Date/Time:</span>
          <span>{order?.OrderDate || "N/A"}</span>
        </div>
      </div>


      <div className="w-full border-y-2 border-black my-1 px-1 py-1">
        <div className="flex justify-between font-medium text-black text-sm">
          <span className="w-[20%]">Qty</span>
          <span className="w-[60%] text-left">Item</span>
          <span className="w-[20%] text-right">Price</span>
        </div>

        <div>{itemBody}</div>
      </div>


   <div className="w-full flex flex-col text-black text-sm">
  {/* Row: Subtotal */}
  {order?.TotalAmountWithoutGST > 0 && (
    <div className="flex justify-between items-center py-0">
      <span className="font-normal">Subtotal:</span>
      <span className="font-normal">Rs {order?.TotalAmountWithoutGST}</span>
    </div>
  )}

  {/* Row: GST */}
  {order?.TotalGST > 0 && (
    <div className="flex justify-between items-center py-0">
      <span className="font-normal">Gst {gstPercents}</span>
      <span className="font-normal">Rs {order?.TotalGST}</span>
    </div>
  )}

  {/* Row: Delivery Charges */}
  {order?.DeliveryCharges > 0 && (
    <div className="flex justify-between items-center py-0">
      <span className="font-normal">Delivery Charges</span>
      <span className="font-normal">Rs {order?.DeliveryCharges}</span>
    </div>
  )}

  {/* Row: Discount */}
  {order?.Discount > 0 && (
    <div className="flex justify-between items-center py-0">
      <span className="font-normal">Discount</span>
      <span className="font-normal">Rs {order?.Discount}</span>
    </div>
  )}

  {/* Row: Net Bill (always shown) */}
  <div className="flex justify-between items-center py-0">
    <span className="font-medium">Net Bill:</span>
    <span className="font-medium">Rs {order?.NetAmount || "0.00"}</span>
  </div>

  {/* Row: Payment Mode (always shown) */}
  <div className="flex justify-between items-center py-0">
    <span className="font-medium">Payment Mode:</span>
    <span className="font-medium">{order?.PaymentMode || "N/A"}</span>
  </div>
</div>



      <br />







      <div className="flex flex-col w-full items-center text-black">
        <span className="italic">Powered by EatX</span>
        <span> Printed on: {getPrintTime()}</span>
      </div>





    </div>
  );
});

const ReceiptPrint = ({ orderDetails, orderItems }) => {
  const componentRef = useRef();


  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Receipt_${orderDetails?.OrderNumber || "unknown"}`,
    pageStyle: "@page { size: auto; margin: 0mm; }"
  });

  return (
    <div>
      {/* Hidden receipt template - only visible when printing */}
      <ReceiptTemplate ref={componentRef} order={orderDetails} items={orderItems} />

      {/* Print button - visible on screen */}
      <button type="primary" className="mt-4 no-print bg-red-500 text-white rounded-full px-2 py-1" onClick={handlePrint}>
        Print Receipt
      </button>
    </div>
  );
};

export default ReceiptPrint;